/**
 * @fileoverview Promotional Cover Screen Component for Farmeando Aura on stadium LED screens (/screen).
 * Renders high-impact promotional branding with sponsor/club logos, floating title animations,
 * dynamic theme backgrounds (color, image, video), and contrast-safe typography.
 * Implements a Zero-Canvas architecture with GPU Layer Compositing (0% CPU usage in idle state).
 * 
 * @module components/Screen/Sections/FarmeandoAura/FarmeandoAuraCover
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { GetConfig, GetTheme } from '../../../../utilities/BL/FarmeandoAura';

/**
 * Default fallback colors and configuration when theme is not provided.
 * @constant {Object}
 */
const DEFAULT_COVER_CONFIGURATION = {
    coverTitle: 'FARMEANDO AURA',
    coverBackgroundType: 'color',
    coverBackgroundColor: '#0a0a14',
    coverBackgroundUrl: '',
    coverTextColor: '#ffffff',
    brandLogoUrl: '',
    sponsorLogoUrl: ''
};

/**
 * FarmeandoAuraCover Component
 * 
 * @function FarmeandoAuraCover
 * @param {Object} props - Component properties.
 * @param {Object} [props.payload] - WebSocket payload or initial data from parent Screen router.
 * @returns {React.ReactElement} The rendered stadium cover view.
 */
export const FarmeandoAuraCover = ({ payload }) => {
    // =========================================================================
    // 1. ESTADO DEL TEMA VISUAL ACTIVO
    // =========================================================================
    const [activeThemeData, setActiveThemeData] = useState(null);
    const [isLoadingThemeInformation, setIsLoadingThemeInformation] = useState(false);

    /**
     * Resolves media file paths into fully qualified absolute URLs.
     * 
     * @function resolveMediaUrl
     * @param {string|null} mediaPath - Relative or absolute media location.
     * @returns {string} Fully resolved media URL.
     */
    const resolveMediaUrl = useCallback((mediaPath) => {
        if (!mediaPath) return '';
        if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://') || mediaPath.startsWith('blob:')) {
            return mediaPath;
        }
        const apiBaseEndpointUrl = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        const sanitizedMediaPath = mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath;
        return `${apiBaseEndpointUrl}${sanitizedMediaPath}`;
    }, []);

    /**
     * Fetches the active theme from the backend API if not already present in the incoming payload.
     * 
     * @async
     * @function fetchActiveThemeConfiguration
     */
    const fetchActiveThemeConfiguration = useCallback(async () => {
        setIsLoadingThemeInformation(true);
        try {
            const configurationResponse = await GetConfig();
            if (configurationResponse.success && configurationResponse.data) {
                const roomConfiguration = configurationResponse.data;
                if (roomConfiguration.activeTheme) {
                    setActiveThemeData(roomConfiguration.activeTheme);
                } else if (roomConfiguration.activeThemeId) {
                    const themeResponse = await GetTheme(roomConfiguration.activeThemeId);
                    if (themeResponse.success && themeResponse.data) {
                        setActiveThemeData(themeResponse.data);
                    }
                }
            }
        } catch (themeFetchError) {
            // Non-blocking fallback to default configuration
        } finally {
            setIsLoadingThemeInformation(false);
        }
    }, []);

    // Synchronize theme when payload changes or trigger initial fetch
    useEffect(() => {
        if (payload) {
            const incomingThemeData = payload.state?.currentTheme ||
                payload.data?.currentTheme ||
                payload.payload?.currentTheme ||
                payload.theme ||
                payload.data?.theme;

            if (incomingThemeData) {
                setActiveThemeData(incomingThemeData);
                return;
            }
        }

        if (!activeThemeData) {
            fetchActiveThemeConfiguration();
        }
    }, [payload, activeThemeData, fetchActiveThemeConfiguration]);

    // =========================================================================
    // 2. EXTRACCIÓN DE ATRIBUTOS Y FALLBACKS
    // =========================================================================
    const coverBackgroundType = activeThemeData?.coverBackgroundType || DEFAULT_COVER_CONFIGURATION.coverBackgroundType;
    const coverBackgroundColor = activeThemeData?.coverBackgroundColor || DEFAULT_COVER_CONFIGURATION.coverBackgroundColor;
    const coverBackgroundUrl = activeThemeData?.coverBackgroundUrl ? resolveMediaUrl(activeThemeData.coverBackgroundUrl) : '';
    const coverTextColor = activeThemeData?.coverTextColor || DEFAULT_COVER_CONFIGURATION.coverTextColor;
    const coverTitleText = activeThemeData?.coverTitle || DEFAULT_COVER_CONFIGURATION.coverTitle;
    const brandLogoUrl = activeThemeData?.brandLogoUrl ? resolveMediaUrl(activeThemeData.brandLogoUrl) : '';
    const sponsorLogoUrl = activeThemeData?.sponsorLogoUrl ? resolveMediaUrl(activeThemeData.sponsorLogoUrl) : '';

    // =========================================================================
    // 3. RENDERIZADO DEL FONDO DINÁMICO (ZERO-CANVAS GPU COMPOSITED)
    // =========================================================================
    const renderDynamicBackgroundLayer = () => {
        if (coverBackgroundType === 'video' && coverBackgroundUrl) {
            return (
                <video
                    src={coverBackgroundUrl}
                    className="aura-screen-cover__background-video"
                    autoPlay
                    loop
                    muted
                    playsInline
                />
            );
        }

        if (coverBackgroundType === 'image' && coverBackgroundUrl) {
            return (
                <div
                    className="aura-screen-cover__background-image"
                    style={{ backgroundImage: `url(${coverBackgroundUrl})` }}
                />
            );
        }

        return (
            <div
                className="aura-screen-cover__background-color"
                style={{ backgroundColor: coverBackgroundColor }}
            />
        );
    };

    // =========================================================================
    // 4. RENDERIZADO PRINCIPAL DE LA PORTADA
    // =========================================================================
    return (
        <main
            className="aura-screen-cover"
            role="region"
            aria-label="Portada Promocional Farmeando Aura"
        >
            {/* Capa de Fondo Dinámico */}
            {renderDynamicBackgroundLayer()}

            {/* Capa de Viñeta y Filtro de Contraste GPU */}
            <div className="aura-screen-cover__vignette-overlay" />

            {/* Contenido Central Flotante */}
            <div className="aura-screen-cover__content-wrapper">
                {/* Contenedor de Logotipos (Club / Patrocinador) */}
                <div className="aura-screen-cover__logos-container">
                    {sponsorLogoUrl && (
                        <div className="aura-screen-cover__logo-box aura-screen-cover__logo-box--sponsor">
                            <img
                                src={sponsorLogoUrl}
                                alt="Patrocinador Oficial"
                                className="aura-screen-cover__sponsor-logo"
                            />
                        </div>
                    )}

                    {brandLogoUrl ? (
                        <div className="aura-screen-cover__logo-box aura-screen-cover__logo-box--brand">
                            <img
                                src={brandLogoUrl}
                                alt="Marca / Club"
                                className="aura-screen-cover__brand-logo"
                            />
                        </div>
                    ) : (
                        <div className="aura-screen-cover__logo-box aura-screen-cover__logo-box--brand">
                            <img
                                src="/img/page/adsl-five.png"
                                alt="Logotipo ADSL"
                                className="aura-screen-cover__brand-logo"
                            />
                        </div>
                    )}
                </div>

                {/* Subtítulo Presenta */}
                <span
                    className="aura-screen-cover__presenta-badge"
                    style={{ color: coverTextColor }}
                >
                    PRESENTA
                </span>

                {/* Título Principal de la Dinámica con Animación Flotante y Brillo Neón */}
                <h1
                    className="aura-screen-cover__hero-title"
                    style={{ color: coverTextColor }}
                >
                    {coverTitleText}
                </h1>

                {/* Subtítulo Descriptivo de Arena */}
                {/* <p
                    className="aura-screen-cover__hero-subtitle"
                    style={{ color: coverTextColor }}
                >
                    ¡COMPETENCIA DE TAPPING EN VIVO POR EL AURA DEL ESTADIO!
                </p> */}
            </div>

            {/* Pie de Página Corporativo con Créditos */}
            <footer
                className="aura-screen-cover__footer-credits"
                style={{ color: coverTextColor }}
            >
                Hecho con ♥ por Interaktia
            </footer>
        </main>
    );
};

FarmeandoAuraCover.propTypes = {
    payload: PropTypes.shape({
        type: PropTypes.string,
        state: PropTypes.object,
        data: PropTypes.object,
        payload: PropTypes.object,
        theme: PropTypes.object
    })
};

FarmeandoAuraCover.defaultProps = {
    payload: null
};
